/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { defineMessages, useIntl } from 'react-intl-next';

import type { FlagsStateContainer } from '@confluence/flags';

import {
	type useRemovePublicLinkPermissionsMutationRemovePublicLinkPermissionsMutation as useRemovePublicLinkPermissionsMutationRemovePublicLinkPermissionsMutation$data,
	type useRemovePublicLinkPermissionsMutationRemovePublicLinkPermissionsMutationVariables as useRemovePublicLinkPermissionsMutationRemovePublicLinkPermissionsMutation$variables,
	type RemovePublicLinkPermissionsInput,
	PublicLinkPermissionsObjectType,
	PublicLinkPermissionsType,
} from './__types__/useRemovePublicLinkPermissionsMutationRemovePublicLinkPermissionsMutation';

const i18n = defineMessages({
	errorTitle: {
		id: 'public-links.useRemovePublicLinkPermissionsMutation.error.title',
		defaultMessage: 'We couldn’t change the public link permission.',
		description:
			'Message shown when there is an error while trying to update the public link permission.',
	},
	errorBody: {
		id: 'public-links.useRemovePublicLinkPermissionsMutation.error.body',
		defaultMessage:
			'Open the Share and Restrict dialog to check the current status, or to update the permission.',
		description:
			'Message shown when there is an error while trying to update the public link permission.',
	},
});

export const useRemovePublicLinkPermissionsMutation = ({
	objectId,
	objectType = PublicLinkPermissionsObjectType.CONTENT,
	permissions = [PublicLinkPermissionsType.EDIT],
	onCompleted,
	onError,
	flags,
}: {
	objectId: string;
	objectType?: PublicLinkPermissionsObjectType;
	permissions?: PublicLinkPermissionsType[];
	onCompleted: () => void;
	onError: (error) => void;
	flags: FlagsStateContainer;
}) => {
	const { formatMessage } = useIntl();
	const [removePublicLinkPermissionsMutation, { loading }] = useMutation<
		useRemovePublicLinkPermissionsMutationRemovePublicLinkPermissionsMutation$data,
		useRemovePublicLinkPermissionsMutationRemovePublicLinkPermissionsMutation$variables
	>(
		gql`
			mutation useRemovePublicLinkPermissionsMutationRemovePublicLinkPermissionsMutation(
				$removePublicLinkPermissionsInput: RemovePublicLinkPermissionsInput!
			) {
				removePublicLinkPermissions(input: $removePublicLinkPermissionsInput) {
					success
					errors {
						message
					}
				}
			}
		`,
		{
			onCompleted: () => {
				onCompleted();
			},
			onError: (error) => {
				void flags.showFlag({
					id: 'updatePublicLinkPermissionsErrorFlagId',
					title: formatMessage(i18n.errorTitle),
					description: formatMessage(i18n.errorBody),
					type: 'error',
					close: 'auto',
				});
				onError(error);
			},
		},
	);

	const removePublicLinkPermissions =
		({ objectId, objectType, permissions }: RemovePublicLinkPermissionsInput) =>
		() =>
			removePublicLinkPermissionsMutation({
				variables: {
					removePublicLinkPermissionsInput: {
						objectId,
						objectType,
						permissions,
					},
				},
			});

	return {
		removePublicLinkPermissions: removePublicLinkPermissions({ objectId, objectType, permissions }),
		loading,
	};
};
